<script setup>
import SaveServerAction from '@frontJs/components/actions/projectManagement/dev/SaveServerAction.vue';
import TenantServerTable from '@frontJs/components/tables/projectManagement/dev/TenantServerTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import CalendarBlankIcon from '@iconify-vue/ph/calendar-blank';
import DatabaseIcon from '@iconify-vue/ph/database';
import DesktopIcon from '@iconify-vue/ph/desktop';
import FileTextIcon from '@iconify-vue/ph/file-text';
import GearIcon from '@iconify-vue/ph/gear';
import KeyIcon from '@iconify-vue/ph/key';
import MapPinIcon from '@iconify-vue/ph/map-pin';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import ShieldCheckIcon from '@iconify-vue/ph/shield-check';
import { Head, router } from '@inertiajs/vue3';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CardColumn from '@js/components/cards/CardColumn.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';

const { servers } = defineProps({
    servers: {
        type: Array,
        required: true,
    },
});

function updateData() {
    router.reload();
}

function getBackupContent(server) {
    let content = '';

    if (server.backupHost) {
        content += '<h2>Hôte</h2><ul><li>' + server.backupHost + '</li></ul>';
    }

    if (server.backupLogin) {
        content +=
            "<h2>Nom d'utilisateur</h2><ul><li>" +
            server.backupLogin +
            '</li></ul>';
    }

    if (server.backupPassword) {
        content +=
            '<h2>Mot de passe</h2><ul><li>' +
            server.backupPassword +
            '</li></ul>';
    }

    if (server.backupStorageSpace) {
        content +=
            '<h2>Espace de stockage</h2><ul><li>' +
            server.backupStorageSpace +
            '</li></ul>';
    }

    return content.length === 0 ? 'N/A' : content;
}

function getServerTabName(server) {
    return server.name.toLowerCase().replace(/\s/g, '');
}

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>Serveurs - DEV - Gestion de projet - Datakepler</title>
    </Head>

    <PageTitle> Gestion de projet - DEV - Serveurs </PageTitle>

    <TabLayout
        :tabs="{
            ...servers.reduce((acc, server) => {
                acc[server.name.toLowerCase().replace(/\s/g, '')] = {
                    title: server.name,
                    contentSlotName: getServerTabName(server),
                };
                return acc;
            }, {}),
        }"
    >
        <template
            v-for="(server, index) in servers"
            :key="index"
            #[getServerTabName(server)]
        >
            <div class="flex flex-col gap-4">
                <div class="mb-0 flex justify-end">
                    <SaveServerAction :server="server" :callback="updateData">
                        <template #trigger>
                            <ActionButton
                                size="small"
                                type="primary"
                                title="Modifier"
                                :left-icon="PencilSimpleIcon"
                            >
                                Modifier le serveur
                            </ActionButton>
                        </template>
                    </SaveServerAction>
                </div>

                <CardColumn>
                    <CardGrid :columns="4" title="Informations générales">
                        <MetricCard
                            v-if="server.id !== 8"
                            title="Nom du serveur"
                            :icon="DesktopIcon"
                            :value="server.serverName ?? 'N/A'"
                        />

                        <MetricCard
                            v-if="server.id !== 8"
                            title="Engagement"
                            :icon="CalendarBlankIcon"
                            :value="server.commitment ?? 'N/A'"
                        />

                        <MetricCard
                            v-if="server.id !== 8"
                            title="IPs"
                            :icon="MapPinIcon"
                            :value="server.ips ?? 'N/A'"
                        />

                        <MetricCard
                            v-if="server.id !== 8"
                            title="Bloc IP"
                            :icon="MapPinIcon"
                        >
                            <template #actions>
                                <DisplayContentAction
                                    title="Bloc IP"
                                    :content="server.ipBlock ?? 'N/A'"
                                >
                                    <template #trigger>
                                        <ActionButton
                                            size="small"
                                            title="Voir le bloc IP"
                                            type="primary"
                                        >
                                            Voir les détails
                                        </ActionButton>
                                    </template>
                                </DisplayContentAction>
                            </template>
                        </MetricCard>

                        <MetricCard
                            v-if="server.id !== 8"
                            title="Versions PHP"
                            :icon="GearIcon"
                            :value="server.phpVersions ?? 'N/A'"
                        />

                        <MetricCard
                            v-if="server.id !== 8"
                            title="Backup"
                            :icon="DatabaseIcon"
                        >
                            <template #actions>
                                <DisplayContentAction
                                    v-if="server.id !== 8"
                                    title="Backup"
                                    :content="getBackupContent(server)"
                                >
                                    <template #trigger>
                                        <ActionButton
                                            size="small"
                                            title="Voir la backup"
                                            type="primary"
                                        >
                                            Voir les détails
                                        </ActionButton>
                                    </template>
                                </DisplayContentAction>
                            </template>
                        </MetricCard>

                        <MetricCard title="Infos compl." :icon="FileTextIcon">
                            <template #actions>
                                <DisplayContentAction
                                    title="Informations complémentaires"
                                    :content="
                                        server.additionalInformation ?? 'N/A'
                                    "
                                >
                                    <template #trigger>
                                        <ActionButton
                                            size="small"
                                            title="Voir les informations complémentaires"
                                            type="primary"
                                        >
                                            Voir les détails
                                        </ActionButton>
                                    </template>
                                </DisplayContentAction>
                            </template>
                        </MetricCard>
                    </CardGrid>

                    <CardGrid :columns="4" title="Sécurité">
                        <MetricCard
                            v-if="server.id !== 8"
                            title="SSH limité par IP"
                            :icon="ShieldCheckIcon"
                            type="boolean"
                            :value="server.isSshLimitedByIp"
                        />

                        <MetricCard
                            v-if="server.id !== 8"
                            title="SSH protégé par clé"
                            :icon="KeyIcon"
                            type="boolean"
                            :value="server.isSshProtectedByKey"
                        />

                        <MetricCard
                            v-if="server.id !== 8"
                            title="Virtualmin limité par IP"
                            :icon="ShieldCheckIcon"
                            type="boolean"
                            :value="server.isServerAdminLimitedByIp"
                        />
                    </CardGrid>
                </CardColumn>

                <TenantServerTable :server="server" />
            </div>
        </template>
    </TabLayout>
</template>
